import { useState } from 'react'
import { updateStaffingRow } from '../../api/client'
import { reordered } from '../capacity/sheet'

/**
 * Drag-to-reorder for Dotacion rows, persisted via `sort_order` -- the same
 * field the import and the API already order rows by, so an arrangement
 * survives a reload instead of resetting the moment the page is left.
 *
 * `persistOrder` is exported (not just `handleDrop`) because the row context
 * menu's "add somebody after this row" flow also needs to reposition a row
 * it just created -- same repositioning, a different trigger, so it takes an
 * explicit `base` payload rather than assuming the drag's own `data` closure.
 */
export function useRowReorder({ projectId, data, setData, guard }) {
  const [dragging, setDragging] = useState(null)      // row id being dragged

  // `base` is the freshest payload to start from -- not necessarily the
  // caller's own `data` state, which may still be one render behind whatever
  // just created or moved a row.
  const persistOrder = async (base, order) => {
    const moved = order
      .map((id, index) => ({ id, index }))
      .filter(({ id, index }) => base.rows.find(r => r.id === id)?.sort_order !== index)
    if (!moved.length) { setData(base); return }
    await guard(async () => {
      let fresh = base
      for (const { id, index } of moved) {
        fresh = await updateStaffingRow(projectId, id, { sort_order: index })
      }
      return fresh
    })
  }

  const handleDrop = (rows, targetId) => {
    const sourceId = dragging
    setDragging(null)
    if (sourceId == null || sourceId === targetId) return
    persistOrder(data, reordered(rows.map(r => r.id), sourceId, targetId))
  }

  return { dragging, setDragging, persistOrder, handleDrop }
}
